/**
 * Configuración del WhatsApp oficial (`/activa/oficial/configuracion?seccion=…`), en dos partes:
 * - **Tu cuenta**: Envío · Bajas y respuestas · Conversaciones · Saldo y costos. Lo que decide el comercio.
 * - **Conexión (técnico)**: la sección `cuenta` (conectar el número con Twilio, el diagnóstico, la guía de la
 *   consola, el Sandbox, la baja automática). La usa el equipo de Deenex.
 *
 * Antes la primera pestaña, y la que se abría sola, era la técnica: lo primero que veía una marca en
 * Configuración era «WA_OFICIAL_TRANSPORT», el Account SID y un diagnóstico de 15 ítems. Ahora sólo se abre
 * sola mientras el número no está conectado (es lo único que hay para hacer); la clave `cuenta` no cambia,
 * así los avisos que llevan a «Revisar conexión» siguen llegando.
 *
 * La sección va en la URL (`?seccion=`) y no en un estado local: las alertas del Resumen y los
 * bloqueos de una campaña llevan directo a la sección que hay que tocar.
 *
 * Todo sale de la cuenta del contexto (una sola fuente). Si no se pudo traer, no se muestra un
 * formulario con valores inventados: se dice y se ofrece reintentar. Sin cuenta real (`id: null`)
 * igual se puede guardar Envío, Bajas, Conversaciones y Saldo: el backend crea el documento.
 *
 * Una persona de un cliente (Accesos, rol «comercio») sólo ve «Saldo y costos», y ahí sólo el saldo y
 * los movimientos: la conexión, el envío, las respuestas automáticas y la plata los maneja el equipo.
 *
 * Debajo de «Envío» (donde está la fatiga: a quién no mandarle por un tiempo), el camino a las listas de exclusión
 * (28/10/2026): a quién no mandarle nunca, elegido en cada campaña. Es una página aparte (`RUTA_LISTAS_EXCLUSION`).
 */

import { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertTriangle, ChevronRight, Info, RefreshCw, UserX, Wrench } from "lucide-react";
import { useCuentaOficial } from "../contexto";
import { cuentaDelModo } from "../logic/conexion";
import { Tarjeta } from "../components/Tarjeta";
import SeccionCuenta from "./config/SeccionCuenta";
import SeccionEnvio from "./config/SeccionEnvio";
import SeccionBajas from "./config/SeccionBajas";
import SeccionConversaciones from "./config/SeccionConversaciones";
import SeccionSaldo from "./config/SeccionSaldo";
import { useRolActiva } from "../../campana-nueva/hooks/useEsEquipoDeenex";
import { EsperandoRol } from "../../campana-nueva/components/SoloEquipo";
import { RUTA_LISTAS_EXCLUSION } from "../logic/exclusiones";

type ClaveSeccion = "cuenta" | "envio" | "bajas" | "conversaciones" | "saldo";

/** Lo que decide el comercio, en el orden en que lo piensa. */
const DE_LA_CUENTA: { clave: ClaveSeccion; label: string }[] = [
  { clave: "envio", label: "Envío" },
  { clave: "bajas", label: "Bajas y respuestas" },
  { clave: "conversaciones", label: "Conversaciones" },
  { clave: "saldo", label: "Saldo y costos" },
];
const TECNICA: { clave: ClaveSeccion; label: string } = { clave: "cuenta", label: "Conexión (técnico)" };
const SECCIONES = [...DE_LA_CUENTA, TECNICA];
/** Lo único de Configuración que ve una persona de un cliente: su saldo y sus movimientos. */
const DEL_CLIENTE = DE_LA_CUENTA.filter((s) => s.clave === "saldo");

export default function ConfiguracionOficialPage() {
  const [params, setParams] = useSearchParams();
  const { cargando, cuenta, error, recargar, tieneCuenta, modo } = useCuentaOficial();
  const pedida = params.get("seccion");
  const rol = useRolActiva();
  const equipo = rol === "equipo";
  const pedidaValida = (equipo ? SECCIONES : DEL_CLIENTE).some((s) => s.clave === pedida);
  // Sin sección pedida: la conexión mientras el número no está conectado (no hay otra cosa que hacer antes) o la
  // marca está frenada (ahí está la tarjeta para reanudarla, y el punto del menú avisa por eso), y si no, lo de la
  // cuenta. «Conectado» con la misma regla que el «Para empezar» del Resumen (`cuentaDelModo`): que exista el
  // documento no alcanza, se crea al guardar Envío sin cuenta.
  const porDefecto: ClaveSeccion = !equipo ? "saldo" : cuentaDelModo(cuenta, modo) && !cuenta?.frenada?.activa ? "envio" : "cuenta";
  const seccion: ClaveSeccion = pedidaValida ? (pedida as ClaveSeccion) : porDefecto;

  // La sección por defecto se decide UNA vez, con la cuenta ya cargada, y queda en la URL. Recalcularla en cada
  // render la cambiaba sola: a mitad de «Conectar» la cuenta pasa a verificada (antes del paso de la URL de
  // entrada), `recargar()` la trae y la página saltaba a Envío; se desmontaba la conexión con la pregunta
  // «¿reemplazar la URL de entrada?» y el token que esperaba esa respuesta, y las respuestas y las BAJAs
  // podían quedar sin configurar sin que nada lo dijera. Tampoco con el rol pendiente (el primer canje en camino):
  // se fijaría «saldo» en la URL de alguien del equipo.
  useEffect(() => {
    if (rol === "pendiente" || pedidaValida || cargando || !cuenta) return;
    setParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set("seccion", porDefecto);
      return p;
    }, { replace: true });
  }, [rol, pedidaValida, cargando, cuenta, porDefecto, setParams]);

  const esqueleto = (
    <div className="space-y-3" aria-busy="true" aria-label="Cargando la configuración">
      <div className="h-20 rounded-2xl bg-slate-100 animate-pulse" />
      <div className="h-64 rounded-2xl bg-slate-100 animate-pulse" />
    </div>
  );

  const ir = (clave: ClaveSeccion) => {
    const p = new URLSearchParams(params);
    p.set("seccion", clave);
    setParams(p, { replace: true });
  };

  return (
    <div className="space-y-5">
      <h2 className="text-xl font-bold text-slate-900 tracking-tight">Configuración</h2>
      {/* Las pestañas envuelven en vez de scrollear de costado: en el celular «Saldo y costos» y «Conexión
          (técnico)» quedaban fuera de la pantalla, sin nada que dijera que estaban. */}
      <div role="tablist" aria-label="Secciones de configuración" className="flex flex-wrap items-center gap-2">
        <div className="inline-flex flex-wrap rounded-xl bg-slate-100 p-1 gap-1">
          {(equipo ? DE_LA_CUENTA : DEL_CLIENTE).map((s) => (
            <button key={s.clave} type="button" role="tab" aria-selected={seccion === s.clave} onClick={() => ir(s.clave)}
              className={`rounded-lg px-3.5 py-1.5 text-sm font-medium transition-colors ${seccion === s.clave ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>
              {s.label}
            </button>
          ))}
        </div>
        {/* Aparte y con otro aspecto: no es «una más» de la cuenta, es la instalación. Sólo para el equipo. */}
        {equipo && (
          <button type="button" role="tab" aria-selected={seccion === TECNICA.clave} onClick={() => ir(TECNICA.clave)}
            className={`inline-flex items-center gap-1.5 rounded-xl border px-3.5 py-2 text-sm font-medium transition-colors ${seccion === TECNICA.clave ? "border-slate-300 bg-white text-slate-900 shadow-sm" : "border-dashed border-slate-300 text-slate-500 hover:text-slate-900"}`}>
            <Wrench size={14} aria-hidden="true" /> {TECNICA.label}
          </button>
        )}
      </div>

      {rol === "pendiente" ? (
        // Si el canje falla, dice qué pasó y «Reintentar» (`EsperandoRol`): antes el esqueleto quedaba para siempre.
        <EsperandoRol cargando={esqueleto} />
      ) : cargando && !cuenta ? esqueleto : !cuenta ? (
        <Tarjeta>
          <p className="text-sm font-semibold text-red-700 flex items-center gap-1.5"><AlertTriangle size={15} /> No pudimos traer la configuración.</p>
          <p className="text-sm text-slate-600 mt-1">{error || "Probá de nuevo en un rato."}</p>
          <button type="button" onClick={() => void recargar()} className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-violet-700 hover:underline">
            <RefreshCw size={14} /> Reintentar
          </button>
        </Tarjeta>
      ) : (
        <>
          {equipo && !tieneCuenta && seccion !== "cuenta" && (
            <p className="flex items-start gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
              <Info size={15} className="flex-shrink-0 mt-0.5" />
              Tu número de WhatsApp todavía no está conectado. Igual podés dejar esto configurado: se guarda para cuando se conecte.
            </p>
          )}
          {seccion === "cuenta" && (
            <>
              <p className="flex items-start gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                <Wrench size={15} className="flex-shrink-0 mt-0.5" />
                Para el equipo de Deenex: acá se conecta el número con Twilio, se revisa que la conexión ande y se activa la baja automática.
              </p>
              <SeccionCuenta />
            </>
          )}
          {seccion === "envio" && (
            <>
              <SeccionEnvio />
              <Link to={RUTA_LISTAS_EXCLUSION} data-testid="link-listas-exclusion"
                className="flex items-center justify-between gap-3 rounded-2xl border border-slate-100 bg-white p-5 shadow-sm hover:border-violet-200">
                <span className="flex items-start gap-3">
                  <UserX size={18} className="mt-0.5 shrink-0 text-violet-600" />
                  <span>
                    <span className="block text-sm font-semibold text-slate-900">Listas de exclusión</span>
                    <span className="block text-sm text-slate-500">
                      La gente que nunca tiene que recibir una promoción (empleados, clientes VIP): se arma una vez y se elige en el
                      paso Audiencia de cada campaña.
                    </span>
                  </span>
                </span>
                <ChevronRight size={16} className="shrink-0 text-slate-400" />
              </Link>
            </>
          )}
          {seccion === "bajas" && <SeccionBajas />}
          {seccion === "conversaciones" && <SeccionConversaciones />}
          {seccion === "saldo" && <SeccionSaldo />}
        </>
      )}
    </div>
  );
}
